import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Bot, Users, Loader2, Trash2, Globe, Sparkles, AlertTriangle,
  Search, Settings2, X, MapPin, Zap,
} from 'lucide-react';
import { api, compact, timeAgo } from '../../lib/api';
import { ARENA, themeOf } from '../../lib/classTheme';
import { ArenaTitle, ArenaTabs, ArenaEmpty, ArenaStatBar } from '../../components/ArenaFrame';
import { Avatar, Sheet, SkeletonList } from '../../components/ui';
import { flagOf } from '../../lib/countries';

export default function AdminNPC() {
  const qc = useQueryClient();

  const [tab, setTab] = useState('cities');
  const [genOpen, setGenOpen] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);

  /* ---- city browsing ---- */
  const [citySearch, setCitySearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');

  /* ---- npc list ---- */
  const [npcSearch, setNpcSearch] = useState('');
  const [npcDebounced, setNpcDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(citySearch), 400);
    return () => clearTimeout(t);
  }, [citySearch]);

  useEffect(() => {
    const t = setTimeout(() => setNpcDebounced(npcSearch), 400);
    return () => clearTimeout(t);
  }, [npcSearch]);

  /* ============================================================
     QUERIES
     ============================================================ */
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['npcOverview', debounced, countryFilter, cityFilter],
    queryFn: () => api.get('/npc/overview', {
      params: {
        q: debounced || undefined,
        country: countryFilter || undefined,
        filter: cityFilter || undefined,
        limit: 80,
      },
    }).then(r => r.data),
    keepPreviousData: true,
  });

  const { data: list, isLoading: listLoading } = useQuery({
    queryKey: ['npcList', npcDebounced],
    queryFn: () => api.get('/npc/list', {
      params: { q: npcDebounced || undefined },
    }).then(r => r.data),
    enabled: tab === 'list',
    keepPreviousData: true,
  });

  /* ============================================================
     MUTATIONS
     ============================================================ */
  const gen = useMutation({
    mutationFn: (body) => api.post('/npc/generate', body),
    onSuccess: ({ data: d }) => {
      toast.success(
        `✅ ${d.created} NPCs created in ${d.city}${d.failed ? ` · ${d.failed} failed` : ''}`,
        { duration: 6000 });
      setGenOpen(null);
      qc.invalidateQueries({ queryKey: ['npcOverview'] });
      qc.invalidateQueries({ queryKey: ['npcList'] });
    },
    onError: e => toast.error(e.message),
  });

  const delCity = useMutation({
    mutationFn: (id) => api.delete(`/npc/city/${id}`, { data: { confirm: 'DELETE_NPCS' } }),
    onSuccess: ({ data: d }) => {
      toast.success(`${d.deleted} NPCs removed`);
      qc.invalidateQueries({ queryKey: ['npcOverview'] });
    },
    onError: e => toast.error(e.message),
  });

  const delAll = useMutation({
    mutationFn: () => api.delete('/npc/all', { data: { confirm: 'DELETE_ALL_NPCS' } }),
    onSuccess: ({ data: d }) => {
      toast.success(`${d.deleted} NPCs removed globally`);
      qc.invalidateQueries({ queryKey: ['npcOverview'] });
      qc.invalidateQueries({ queryKey: ['npcList'] });
    },
    onError: e => toast.error(e.message),
  });

  const saveCfg = useMutation({
    mutationFn: (value) => api.put('/npc/config', { value }),
    onSuccess: () => {
      toast.success('Settings saved');
      setCfgOpen(false);
      qc.invalidateQueries({ queryKey: ['npcOverview'] });
    },
    onError: e => toast.error(e.message),
  });

  if (isLoading || !data) return <SkeletonList n={4} h="h-28" />;

  const ready = data.readiness;
  const canGenerate = ready.heroes > 0;
  const cities = data.cities || [];
  const hasFilters = !!(debounced || countryFilter || cityFilter);

  return (
    <div className="space-y-4">
      {/* ═══════════ HEADER ═══════════ */}
      <div className="card-arena p-4 flex items-center gap-3">
        <div className="w-11 h-11 grid place-items-center shrink-0"
          style={{
            borderRadius: 'var(--radius)',
            background: 'var(--accent-soft)',
            border: `1px solid ${ARENA.gold}`,
          }}>
          <Bot size={20} style={{ color: ARENA.arenaText }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-extrabold neon-glow-text"
            style={{ color: ARENA.arenaText }}>
            NPC Population
          </h2>
          <p className="text-[11px] font-semibold" style={{ color: ARENA.arenaTextSoft }}>
            {compact(data.totals.npcs)} NPCs · {data.totals.cities_populated} of{' '}
            {compact(data.totals.total_cities)} cities
          </p>
        </div>
        <button onClick={() => setCfgOpen(true)} className="btn-ghost btn-sm shrink-0">
          <Settings2 size={13} />
        </button>
      </div>

      {!canGenerate && (
        <div className="card p-4 flex gap-3" style={{ borderColor: ARENA.blood, borderWidth: 2 }}>
          <AlertTriangle size={20} style={{ color: ARENA.blood }} className="shrink-0" />
          <div>
            <p className="font-display font-bold text-sm">No hero templates ready</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Generate hero portraits first in AI Studio.
            </p>
          </div>
        </div>
      )}

      {/* ═══════════ STATS ═══════════ */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <Stat v={compact(data.totals.npcs)} l="NPCs" hi />
        <Stat v={compact(data.totals.real_users)} l="Real" />
        <Stat v={compact(data.totals.npc_heroes)} l="Heroes" />
        <Stat v={compact(data.totals.npc_pets)} l="Pets" />
        <Stat v={compact(data.totals.npc_cars)} l="Cars" />
        <Stat v={compact(data.totals.name_pool)} l="Names" />
      </div>

      {/* ═══════════ READINESS ═══════════ */}
      <div className="card p-3.5">
        <ArenaTitle title="Asset pool" latin="Copia"
          sub="NPCs reuse existing artwork — zero AI cost" />
        <div className="grid grid-cols-3 gap-2">
          <Ready label="Heroes" v={ready.heroes} required />
          <Ready label="Pets" v={ready.pets} />
          <Ready label="Cars" v={ready.cars} />
        </div>
      </div>

      <ArenaTabs value={tab} onChange={setTab} tabs={[
        { key: 'cities', label: 'Cities', icon: Globe },
        { key: 'list', label: 'NPCs', icon: Users },
      ]} />

      {/* ═══════════════════════════════════════════
          CITIES
          ═══════════════════════════════════════════ */}
      {tab === 'cities' && (
        <>
          {/* ── FILTERS ── */}
          <div className="space-y-2">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-muted)' }} />
              <input className="input pl-9 pr-9" autoComplete="off"
                placeholder="Search any city worldwide — Iasi, Cluj, Berlin, Tokyo…"
                value={citySearch} onChange={e => setCitySearch(e.target.value)} />
              {isFetching && (
                <Loader2 size={14} className="absolute right-9 top-1/2 -translate-y-1/2 animate-spin"
                  style={{ color: 'var(--text-muted)' }} />
              )}
              {citySearch && (
                <button onClick={() => setCitySearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: 'var(--text-muted)' }}>
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
              <FBtn active={!cityFilter} onClick={() => setCityFilter('')} label="All" />
              <FBtn active={cityFilter === 'empty'} onClick={() => setCityFilter('empty')}
                label="Not populated" />
              <FBtn active={cityFilter === 'populated'} onClick={() => setCityFilter('populated')}
                label="Populated" />
            </div>

            {data.countries?.length > 0 && (
              <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
                <FBtn active={!countryFilter} onClick={() => setCountryFilter('')} label="🌍 All" />
                {data.countries.slice(0, 24).map(c => (
                  <FBtn key={c.country_code}
                    active={countryFilter === c.country_code}
                    onClick={() => setCountryFilter(
                      countryFilter === c.country_code ? '' : c.country_code)}
                    label={`${flagOf(c.country_code)} ${c.country_code}`}
                    badge={c.npcs > 0 ? compact(c.npcs) : null} />
                ))}
              </div>
            )}

            <div className="flex items-center justify-between px-1">
              <p className="text-[11px] font-semibold" style={{ color: 'var(--text-muted)' }}>
                Showing <b>{cities.length}</b> of <b>{compact(data.citiesTotal)}</b> cities
                {data.hasMore && ' · refine to see more'}
              </p>
              {hasFilters && (
                <button onClick={() => { setCitySearch(''); setCountryFilter(''); setCityFilter(''); }}
                  className="text-[11px] font-bold" style={{ color: ARENA.goldD }}>
                  Clear filters
                </button>
              )}
            </div>
          </div>

          <ArenaTitle title="Populate cities" latin="Popula"
            sub={`${data.config?.ratio_pct}% of population · max ${data.config?.max_per_city} per city`} />

          {/* ── LIST ── */}
          {!cities.length ? (
            <ArenaEmpty emoji="🔍" title="No cities found"
              sub="Try a different search term or clear the filters."
              action={hasFilters && (
                <button onClick={() => { setCitySearch(''); setCountryFilter(''); setCityFilter(''); }}
                  className="btn-ghost btn-sm">Clear filters</button>
              )} />
          ) : (
            <div className="space-y-2">
              {cities.map(c => {
                const target = Math.max(
                  data.config?.min_per_city || 8,
                  Math.min(data.config?.max_per_city || 400,
                    Math.round((c.population || 0) * (data.config?.ratio_pct || 3) / 100)));
                const pct = target ? Math.min(100, (c.npc_count / target) * 100) : 0;
                const done = c.npc_count >= target;

                return (
                  <div key={c.id} className="card p-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xl shrink-0">
                        {c.is_capital ? '⭐' : flagOf(c.country_code)}
                      </span>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="font-display font-bold text-sm truncate">{c.handle}</p>
                          {done && <span className="chip-mint !text-[8px]">FULL</span>}
                          {c.is_capital && <span className="chip-gold !text-[8px]">CAPITAL</span>}
                        </div>
                        <p className="text-[11px] font-semibold truncate"
                          style={{ color: 'var(--text-muted)' }}>
                          {c.name}
                          {c.region && ` · ${c.region}`}
                          {c.population > 0 && ` · ${compact(c.population)} people`}
                          {c.real_users > 0 && ` · ${c.real_users} real`}
                        </p>
                        {c.npc_generated_at && (
                          <p className="text-[10px]" style={{ color: 'var(--text-muted)', opacity: .7 }}>
                            last run {timeAgo(c.npc_generated_at)}
                          </p>
                        )}
                      </div>

                      <div className="text-right shrink-0">
                        <p className="font-display font-black text-sm"
                          style={{ color: done ? 'var(--success)' : ARENA.goldD }}>
                          {c.npc_count}
                          <span className="text-[10px] opacity-60">/{target}</span>
                        </p>
                        <p className="text-[9px] font-bold" style={{ color: 'var(--text-muted)' }}>
                          NPCs
                        </p>
                      </div>
                    </div>

                    <div className="mt-2">
                      <ArenaStatBar value={pct} max={100} height={5}
                        color={done ? 'linear-gradient(90deg,#34d399,#059669)' : undefined} />
                    </div>

                    <div className="flex gap-1.5 mt-2">
                      <button onClick={() => setGenOpen({ ...c, target })}
                        disabled={!canGenerate || done}
                        className="btn-primary btn-sm flex-1">
                        <Sparkles size={12} />
                        {done ? 'Complete' : `Generate ${target - c.npc_count}`}
                      </button>
                      {c.npc_count > 0 && (
                        <button
                          onClick={() => confirm(`Delete all ${c.npc_count} NPCs in ${c.handle}?`)
                            && delCity.mutate(c.id)}
                          disabled={delCity.isPending}
                          className="btn btn-sm"
                          style={{ background: 'rgba(239,68,68,.10)', color: ARENA.blood }}>
                          {delCity.isPending
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Trash2 size={12} />}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {data.totals.npcs > 0 && (
            <button
              onClick={() => {
                if (prompt('Type DELETE to remove ALL NPCs globally') === 'DELETE') delAll.mutate();
              }}
              disabled={delAll.isPending}
              className="btn w-full btn-sm"
              style={{ background: 'rgba(239,68,68,.08)', color: ARENA.blood }}>
              {delAll.isPending
                ? <><Loader2 size={13} className="animate-spin" /> Deleting…</>
                : <><Trash2 size={13} /> Delete all {compact(data.totals.npcs)} NPCs</>}
            </button>
          )}
        </>
      )}

      {/* ═══════════════════════════════════════════
          NPC LIST
          ═══════════════════════════════════════════ */}
      {tab === 'list' && (
        <>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--text-muted)' }} />
            <input className="input pl-9 pr-9" autoComplete="off"
              placeholder="Search NPCs by name or username…"
              value={npcSearch} onChange={e => setNpcSearch(e.target.value)} />
            {npcSearch && (
              <button onClick={() => setNpcSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--text-muted)' }}>
                <X size={14} />
              </button>
            )}
          </div>

          {listLoading && !list ? <SkeletonList n={6} h="h-16" />
            : !list?.length
              ? <ArenaEmpty emoji="🤖" title="No NPCs found"
                  sub={npcDebounced
                    ? 'Try another search term.'
                    : 'Populate a city to create AI residents.'} />
              : (
                <>
                  <p className="text-[11px] font-semibold px-1" style={{ color: 'var(--text-muted)' }}>
                    {list.length} NPC{list.length !== 1 ? 's' : ''}
                    {list.length >= 200 && ' (showing first 200)'}
                  </p>
                  <div className="card p-2 space-y-1">
                    {list.map(n => {
                      const t = themeOf(n.class_key);
                      return (
                        <div key={n.id} className="flex items-center gap-3 p-2.5 transition"
                          style={{ borderRadius: 'var(--radius-sm)' }}>
                          <Avatar src={n.image_url || n.avatar_url} name={n.username}
                            size={36} square gold />

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="font-display font-bold text-sm truncate">
                                {n.display_name}
                              </p>
                              <Bot size={11} style={{ color: 'var(--text-muted)' }} />
                            </div>
                            <p className="text-[11px] font-semibold truncate"
                              style={{ color: 'var(--text-muted)' }}>
                              @{n.username} · {t.icon} L{n.level} · {n.elo} ELO
                              {n.city_handle && ` · ${n.city_handle}`}
                            </p>
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            {n.pets > 0 && <span className="chip-ink !px-1.5">🐾</span>}
                            {n.cars > 0 && <span className="chip-ink !px-1.5">🏎️</span>}
                            <span className="chip-brand">💎{compact(n.points_total)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
        </>
      )}

      {/* ═══════════ SHEETS ═══════════ */}
      <GenerateSheet
        city={genOpen}
        onClose={() => setGenOpen(null)}
        config={data.config}
        readiness={ready}
        onSubmit={(b) => gen.mutate(b)}
        busy={gen.isPending} />

      <ConfigSheet
        open={cfgOpen}
        onClose={() => setCfgOpen(false)}
        config={data.config}
        onSave={(v) => saveCfg.mutate(v)}
        busy={saveCfg.isPending} />
    </div>
  );
}

/* ============================================================
   GENERATE SHEET
   ============================================================ */
function GenerateSheet({ city, onClose, config, readiness, onSubmit, busy }) {
  const [count, setCount] = useState(null);

  useEffect(() => { setCount(null); }, [city?.id]);

  if (!city) return null;

  const remaining = Math.max(0, city.target - city.npc_count);
  const target = count ?? remaining;
  const eta = Math.max(1, Math.ceil(target / 20));

  const quick = [10, 50, 100, 250, remaining]
    .filter((v, i, a) => v > 0 && v <= (config?.max_per_city || 400) && a.indexOf(v) === i);

  return (
    <Sheet open={!!city} onClose={onClose} title={`Populate ${city.handle}`}>
      <div className="space-y-4">
        {/* city info */}
        <div className="flex items-center gap-3 p-3"
          style={{
            borderRadius: 'var(--radius)',
            background: 'var(--surface-alt)',
            border: '1px solid var(--border)',
          }}>
          <span className="text-2xl">{city.is_capital ? '⭐' : flagOf(city.country_code)}</span>
          <div className="min-w-0">
            <p className="font-display font-bold text-sm truncate">{city.name}</p>
            <p className="text-[11px] font-semibold" style={{ color: 'var(--text-muted)' }}>
              {city.handle}{city.region && ` · ${city.region}`}
            </p>
          </div>
        </div>

        <div className="card p-3">
          <div className="grid grid-cols-2 gap-2 text-center">
            <Meta l="Population" v={compact(city.population)} />
            <Meta l="Current NPCs" v={city.npc_count} />
            <Meta l="Target" v={city.target} />
            <Meta l="To create" v={target} hi />
          </div>
        </div>

        {/* slider */}
        <div>
          <div className="flex justify-between mb-1.5">
            <label className="label !mb-0">How many to create</label>
            <span className="font-display font-black text-sm" style={{ color: ARENA.goldD }}>
              {target}
            </span>
          </div>
          <input type="range" min="1" max={Math.max(1, config?.max_per_city || 400)}
            value={target} onChange={e => setCount(Number(e.target.value))}
            className="w-full" style={{ accentColor: ARENA.gold }} />

          {quick.length > 0 && (
            <div className="flex gap-1.5 mt-2">
              {quick.map(v => (
                <button key={v} onClick={() => setCount(v)}
                  className="btn-ghost btn-sm flex-1"
                  style={target === v
                    ? { borderColor: ARENA.gold, color: ARENA.goldD }
                    : {}}>
                  {v === remaining ? 'All' : v}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* what they get */}
        <div className="card p-3">
          <p className="label">Each NPC receives</p>
          <ul className="text-xs space-y-1" style={{ color: 'var(--text-soft)' }}>
            <li>👤 A local name for <b>{city.country_code || 'their country'}</b></li>
            <li>⚔️ A random hero from <b>{readiness.heroes}</b> templates</li>
            <li>📊 Level 1–{config?.level_max || 22} with scaled stats and ELO</li>
            <li>
              🐾 {Math.round((config?.pet_chance ?? .3) * 100)}% chance of a companion
              {readiness.pets === 0 && (
                <span style={{ color: ARENA.blood }}> — none ready</span>
              )}
            </li>
            <li>
              🏎️ {Math.round((config?.car_chance ?? .2) * 100)}% chance of a chariot
              {readiness.cars === 0 && (
                <span style={{ color: ARENA.blood }}> — none ready</span>
              )}
            </li>
          </ul>

          <div className="flex items-center gap-2 mt-2.5 text-[11px] font-bold flex-wrap">
            <span style={{ color: 'var(--success)' }}>💰 Zero AI cost</span>
            <span style={{ color: 'var(--text-muted)' }}>·</span>
            <span style={{ color: 'var(--text-muted)' }}>⏱ ~{eta} min</span>
            <span style={{ color: 'var(--text-muted)' }}>·</span>
            <span style={{ color: 'var(--text-muted)' }}>reuses existing artwork</span>
          </div>
        </div>

        {target > 200 && (
          <div className="p-3 flex gap-2"
            style={{
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(245,158,11,.10)',
              border: '1px solid rgba(245,158,11,.3)',
            }}>
            <Zap size={15} style={{ color: '#f59e0b' }} className="shrink-0" />
            <p className="text-[11px] font-bold" style={{ color: '#92400e' }}>
              Large batch — this may take a few minutes. Keep the page open.
            </p>
          </div>
        )}

        <button onClick={() => onSubmit({ cityGroupId: city.id, count: target })}
          disabled={busy || target < 1}
          className="btn-primary w-full py-3">
          {busy
            ? <><Loader2 size={16} className="animate-spin" /> Creating {target} NPCs…</>
            : <><Sparkles size={16} /> Generate {target} NPCs</>}
        </button>
      </div>
    </Sheet>
  );
}

/* ============================================================
   CONFIG SHEET
   ============================================================ */
function ConfigSheet({ open, onClose, config, onSave, busy }) {
  const [c, setC] = useState(config || {});

  useEffect(() => { if (open) setC(config || {}); }, [open, config]);

  const FIELDS = [
    { k: 'ratio_pct',    l: 'Population ratio',  min: 0.1, max: 10,   step: 0.1, suffix: '%' },
    { k: 'max_per_city', l: 'Max per city',      min: 10,  max: 2000, step: 10 },
    { k: 'min_per_city', l: 'Min per city',      min: 1,   max: 100,  step: 1 },
    { k: 'level_max',    l: 'Max hero level',    min: 5,   max: 60,   step: 1 },
    { k: 'pet_chance',   l: 'Companion chance',  min: 0,   max: 1,    step: 0.05, pct: true },
    { k: 'car_chance',   l: 'Chariot chance',    min: 0,   max: 1,    step: 0.05, pct: true },
  ];

  return (
    <Sheet open={open} onClose={onClose} title="NPC settings">
      <div className="space-y-3.5">
        {FIELDS.map(f => (
          <div key={f.k}>
            <div className="flex justify-between mb-1">
              <label className="label !mb-0">{f.l}</label>
              <span className="font-display font-black text-sm" style={{ color: ARENA.goldD }}>
                {f.pct
                  ? `${Math.round((c[f.k] ?? 0) * 100)}%`
                  : `${c[f.k] ?? f.min}${f.suffix || ''}`}
              </span>
            </div>
            <input type="range" min={f.min} max={f.max} step={f.step}
              value={c[f.k] ?? f.min}
              onChange={e => setC({ ...c, [f.k]: Number(e.target.value) })}
              className="w-full" style={{ accentColor: ARENA.gold }} />
          </div>
        ))}

        <label className="flex items-center gap-2 text-sm font-bold pt-1">
          <input type="checkbox" checked={c.challengeable !== false}
            onChange={e => setC({ ...c, challengeable: e.target.checked })}
            className="w-4 h-4" style={{ accentColor: ARENA.gold }} />
          NPCs can be challenged to duels and races
        </label>

        <label className="flex items-center gap-2 text-sm font-bold">
          <input type="checkbox" checked={!!c.chat_enabled}
            onChange={e => setC({ ...c, chat_enabled: e.target.checked })}
            className="w-4 h-4" style={{ accentColor: ARENA.gold }} />
          NPCs post occasionally in city chats
        </label>

        <div className="p-3" style={{
          borderRadius: 'var(--radius-sm)',
          background: 'var(--surface-alt)',
        }}>
          <p className="text-[11px] font-bold" style={{ color: 'var(--text-soft)' }}>
            💡 Example: a city of 1,000,000 people at {c.ratio_pct ?? 3}% would target{' '}
            <b>{Math.min(c.max_per_city ?? 400,
              Math.round(1000000 * (c.ratio_pct ?? 3) / 100)).toLocaleString()}</b> NPCs
            (capped at {c.max_per_city ?? 400}).
          </p>
        </div>

        <button onClick={() => onSave(c)} disabled={busy} className="btn-primary w-full">
          {busy ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : 'Save settings'}
        </button>
      </div>
    </Sheet>
  );
}

/* ============================================================
   SMALL BITS
   ============================================================ */
function FBtn({ active, onClick, label, badge }) {
  return (
    <button onClick={onClick}
      className="relative px-3 py-1.5 text-xs font-bold whitespace-nowrap transition shrink-0"
      style={{
        borderRadius: 'var(--radius-sm)',
        background: active ? ARENA.goldFrame : 'var(--surface)',
        color: active ? '#fff' : 'var(--text-muted)',
        border: `1px solid ${active ? 'transparent' : 'var(--border)'}`,
        boxShadow: active ? 'var(--shadow-glow)' : 'none',
      }}>
      {label}
      {badge && (
        <span className="ml-1 opacity-70">({badge})</span>
      )}
    </button>
  );
}

function Stat({ v, l, hi }) {
  return (
    <div className="card p-2.5 text-center">
      <p className="font-display font-black text-base"
        style={{ color: hi ? ARENA.goldD : 'var(--text)' }}>{v}</p>
      <p className="text-[9px] font-display font-bold uppercase"
        style={{ color: 'var(--text-muted)' }}>{l}</p>
    </div>
  );
}

function Ready({ label, v, required }) {
  const ok = v > 0;
  return (
    <div className="p-2.5 text-center"
      style={{
        borderRadius: 'var(--radius-sm)',
        background: ok ? 'rgba(16,185,129,.10)' : 'rgba(239,68,68,.08)',
        border: `1px solid ${ok ? 'rgba(16,185,129,.3)' : 'rgba(239,68,68,.25)'}`,
      }}>
      <p className="font-display font-black text-lg"
        style={{ color: ok ? 'var(--success)' : ARENA.blood }}>{v}</p>
      <p className="text-[9px] font-display font-bold uppercase"
        style={{ color: 'var(--text-muted)' }}>
        {label}{required && !ok && ' *'}
      </p>
    </div>
  );
}

function Meta({ l, v, hi }) {
  return (
    <div className="p-2" style={{
      borderRadius: 'var(--radius-sm)',
      background: 'var(--surface-alt)',
    }}>
      <p className="text-[9px] font-display font-black uppercase"
        style={{ color: 'var(--text-muted)' }}>{l}</p>
      <p className="font-display font-black text-base mt-0.5"
        style={{ color: hi ? ARENA.goldD : 'var(--text)' }}>{v}</p>
    </div>
  );
}